import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Animated product stage. Slides are rendered in Liquid; this only moves between them.
 * Auto-advance pauses on hover, focus, touch, hidden tab, and is off for reduced motion.
 */
class BekofyHero extends HTMLElement {
  #index = 0;
  #timer = 0;
  #start = 0;
  #remaining = 0;
  #paused = false;
  #touchX = null;

  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-bk-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-bk-thumb]')];
    if (this.slides.length < 2) return;

    this.duration = Number(this.dataset.duration || 5500);
    this.style.setProperty('--bk-hero-duration', `${this.duration}ms`);
    this.#remaining = this.duration;
    this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.go(i, true)));
    this.querySelector('[data-bk-prev]')?.addEventListener('click', () => this.step(-1, true));
    this.querySelector('[data-bk-next]')?.addEventListener('click', () => this.step(1, true));

    this.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && this.pause());
    this.addEventListener('pointerleave', (e) => e.pointerType === 'mouse' && this.resume());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', (e) => !this.contains(e.relatedTarget) && this.resume());
    this.addEventListener('touchstart', (e) => { this.#touchX = e.touches[0].clientX; this.pause(); }, { passive: true });
    this.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - (this.#touchX ?? 0);
      const rtl = document.dir === 'rtl';
      if (Math.abs(dx) > 40) this.step((dx < 0) !== rtl ? 1 : -1, true);
      this.#touchX = null;
      this.resume();
    }, { passive: true });
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const forward = (e.key === 'ArrowRight') !== (document.dir === 'rtl');
      this.step(forward ? 1 : -1, true);
    });
    document.addEventListener('visibilitychange', () => (document.hidden ? this.pause() : this.resume()));

    this.observer = new IntersectionObserver(([entry]) => (entry.isIntersecting ? this.resume() : this.pause()), { threshold: 0.3 });
    this.observer.observe(this);
    this.#schedule();
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.observer?.disconnect();
  }

  get autoplay() {
    return !reduceMotion.matches;
  }

  step(delta, user = false) {
    this.go((this.#index + delta + this.slides.length) % this.slides.length, user);
  }

  go(next, user = false) {
    if (next === this.#index) return;
    const prev = this.#index;
    this.#index = next;
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === next);
      slide.classList.toggle('is-leaving', i === prev);
      slide.setAttribute('aria-hidden', i === next ? 'false' : 'true');
      slide.inert = i !== next;
    });
    this.thumbs.forEach((thumb, i) => {
      thumb.setAttribute('aria-selected', i === next ? 'true' : 'false');
    });
    this.style.setProperty('--bk-hero-accent', this.slides[next].dataset.accent || '');
    clearTimeout(this.leaveTimer);
    this.leaveTimer = setTimeout(() => this.slides[prev].classList.remove('is-leaving'), 700);
    this.#remaining = this.duration;
    if (user) this.dataset.userInteracted = 'true';
    this.#schedule();
  }

  pause() {
    if (this.#paused) return;
    this.#paused = true;
    clearTimeout(this.#timer);
    this.#remaining = Math.max(0, this.#remaining - (performance.now() - this.#start));
    this.classList.add('is-paused');
  }

  resume() {
    if (!this.#paused) return;
    this.#paused = false;
    this.classList.remove('is-paused');
    this.#schedule();
  }

  /* One timer per slide (no per-frame work); the progress ring is a CSS animation of the same length */
  #schedule() {
    clearTimeout(this.#timer);
    if (!this.autoplay || this.#paused) return;
    this.#start = performance.now();
    this.#timer = setTimeout(() => {
      this.#remaining = this.duration;
      this.step(1);
    }, this.#remaining);
  }
}

/** Accessible tabs: all panels are server-rendered, this only switches visibility. */
class BekofyTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (e) => {
        const rtl = document.dir === 'rtl';
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          const forward = (e.key === 'ArrowRight') !== rtl;
          const next = (i + (forward ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        }
      });
    });
  }

  select(index) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      const panel = this.querySelector(`#${tab.getAttribute('aria-controls')}`);
      if (panel) panel.hidden = !active;
    });
  }
}

/** Quick add for single-variant products, using the same cart event contract as the theme's product form. */
async function quickAdd(button) {
  if (button.getAttribute('aria-busy') === 'true') return;
  const variantId = button.dataset.bkAdd;
  button.setAttribute('aria-busy', 'true');

  const ids = String(variantId).split(',').filter(Boolean);
  const formData = new FormData();
  ids.forEach((id, i) => {
    formData.append(`items[${i}][id]`, id);
    formData.append(`items[${i}][quantity]`, '1');
  });
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: ids.map((id) => ({ merchandiseId: id, quantity: 1 })),
      promise: deferred.promise,
    })
  );

  try {
    const cfg = fetchConfig('javascript', { body: formData });
    const response = await fetch(Theme.routes.cart_add_url, { ...cfg, headers: { ...cfg.headers, Accept: 'text/html' } }).then((r) => r.json());
    const ajaxCart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const didError = Boolean(response.status);

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: { items: ajaxCart.items, source: 'bekofy-quick-add', itemCount: ids.length, sections: response.sections, didError },
    });

    if (didError) {
      button.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
      button.classList.add('is-error');
      button.title = response.description || response.message || '';
    } else {
      button.classList.add('is-added');
      setTimeout(() => button.classList.remove('is-added'), 1800);
    }
  } catch (error) {
    deferred.reject(error);
    button.classList.add('is-error');
  } finally {
    button.removeAttribute('aria-busy');
  }
}

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-bk-add]') : null;
  if (!button) return;
  event.preventDefault();
  quickAdd(button);
});

if (!customElements.get('bk-hero')) customElements.define('bk-hero', BekofyHero);
if (!customElements.get('bk-tabs')) customElements.define('bk-tabs', BekofyTabs);

/* Collection page: filter drawer, sort auto-submit, 1/2 column toggle */
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const opener = target.closest('[data-bk-open]');
  if (opener) {
    const dialog = document.getElementById(opener.getAttribute('data-bk-open'));
    if (dialog instanceof HTMLDialogElement) {
      dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
    }
    return;
  }

  if (target.closest('[data-bk-close]') || (target instanceof HTMLDialogElement && target.classList.contains('bk-drawer'))) {
    target.closest('dialog')?.close();
    return;
  }

  const colButton = target.closest('[data-bk-cols]');
  if (colButton) {
    const cols = colButton.getAttribute('data-bk-cols');
    setCols(cols);
    try { localStorage.setItem('bk-cols', cols); } catch (e) {}
  }
});

document.addEventListener('close', (event) => {
  if (event.target instanceof HTMLDialogElement && event.target.classList.contains('bk-drawer')) {
    document.documentElement.style.overflow = '';
  }
}, true);

document.addEventListener('change', (event) => {
  const select = event.target;
  if (select instanceof HTMLSelectElement && select.form?.hasAttribute('data-bk-sort')) select.form.requestSubmit();
});

function setCols(cols) {
  document.querySelectorAll('[data-bk-grid]').forEach((grid) => grid.setAttribute('data-cols', cols));
  document.querySelectorAll('[data-bk-cols]').forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute('data-bk-cols') === cols)));
}

try {
  const saved = localStorage.getItem('bk-cols');
  if (saved) setCols(saved);
} catch (e) {}

/* Cart drawer add-on: re-render after every cart change (Horizon only morphs the item list) */
async function refreshCartAddon() {
  // Horizon re-renders the drawer list after cart changes, so (re)create the slot when needed
  document.querySelectorAll('.cart-drawer__items').forEach((list) => {
    if (!list.querySelector('[data-bk-addon-slot]')) {
      const slot = document.createElement('div');
      slot.setAttribute('data-bk-addon-slot', '');
      list.appendChild(slot);
    }
  });
  if (!document.querySelector('[data-bk-addon-slot]')) return;
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', 'bekofy-cart-addon');
    const html = await fetch(url.toString()).then((r) => r.text());
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('.bk-addon');
    document.querySelectorAll('[data-bk-addon-slot]').forEach((slot) => {
      slot.innerHTML = fresh ? fresh.outerHTML : '';
    });
  } catch (e) {}
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const done = () => setTimeout(refreshCartAddon, 450);
  if (event.promise) event.promise.then(done, done);
  else done();
});
